import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '@shared/services/common-service';
import { NotaFiscalSaidaListarRequest } from '@shared/services/requests/repo-dfe/nota-fiscal-saida-listar.request';
import { NotaFiscalSaidaListarResponse } from '@shared/services/responses/repo-dfe/nota-fiscal-saida-listar.response';
import { NotaFiscalSaidaListarPrintResponse } from '@shared/services/responses/repo-dfe/nota-fiscal-saida-listar-print.response';
import { GetNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/get-nota-fiscal-saida.response';
import { GetNotaFiscalSaidaVisualizacaoResponse } from '@shared/services/responses/orquestrador-vendas/get-nota-fiscal-saida-visualizacao.response';
import { GetNotaFiscalSaidaHistoricoResponse } from '@shared/services/responses/orquestrador-vendas/get-nota-fiscal-saida-historico.response';
import { SalvarNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/salvar-nota-fiscal-saida.response';
import { NFeSaidaConferenciaResponse } from '@shared/services/responses/orquestrador-vendas/nfe-saida-conferencia.response';
import { GetConfiguracoesNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/get-configuracoes-nota-fiscal-saida.response';
import { GetClienteNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/get-cliente-nota-fiscal-saida.response';
import { SalvarNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/salvar-nota-fiscal-saida.request';
import { CriarNotaFiscalSaidaOcorrenciaRequest } from '@shared/services/requests/orquestrador-vendas/criar-nota-fiscal-saida-ocorrencia.request';
import { NotaFiscalSaidaExcluirLoteRequest } from '@shared/services/requests/orquestrador-vendas/nota-fiscal-saida-excluir-lote.request';
import { ExcluirNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/excluir-nota-fiscal-saida.response';
import { NFeSaidaCalculoPreviaRequest } from '@shared/services/requests/orquestrador-vendas/nfe-saida-calculo-previa.request';
import { NFeSaidaCalculoPreviaResponse } from '@shared/services/responses/orquestrador-vendas/nfe-saida-calculo-previa.response';
import { CalculoNFeSaidaResolverResponse } from '@shared/services/responses/motor-fiscal/calculo-nfe-saida-resolver.response';
import { ReturnStruct } from '@shared/services/structs/return.struct';
import { ListarSituacaoNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/listar-situacao-nota-fiscal-saida.request';
import { PrepararTransmissaoLoteNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/preparar-transmissao-lote-nota-fiscal-saida.request';
import { TransmitirNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/transmitir-nota-fiscal-saida.response';
import { ListarSituacaoNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/listar-situacao-nota-fiscal-saida.response';
import { ConsultarRetornoNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/consultar-retorno-nota-fiscal-saida.response';
import { PrepararTransmissaoLoteNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/preparar-transmissao-lote-nota-fiscal-saida.response';
import { LancarFinanceiroNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/lancar-financeiro-nota-fiscal-saida.request';
import { ExcluirFinanceiroNotaFiscalSaidaRequest } from '@shared/services/requests/orquestrador-vendas/excluir-financeiro-nota-fiscal-saida.request';
import { FinanceiroNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/financeiro-nota-fiscal-saida.response';
import { GetFinanceiroNotaFiscalSaidaResponse } from '@shared/services/responses/orquestrador-vendas/get-financeiro-nota-fiscal-saida.response';

/** Controller NotaFiscalSaida do OrquestradorVendas: listagem, relatório e documento da NF-e de saída. */
@Injectable({ providedIn: 'root' })
export class NotaFiscalSaidaService extends CommonService {
  private readonly apiUrl: string = environment.urlApiOrquestradorVendas + 'NotaFiscalSaida';

  constructor(private httpClient: HttpClient) {
    super();
  }

  public listar(request: NotaFiscalSaidaListarRequest, paginaAtual = 0): Observable<NotaFiscalSaidaListarResponse> {
    const params = new HttpParams().set('paginaAtual', paginaAtual.toString());
    return this.httpClient
      .post<NotaFiscalSaidaListarResponse>(`${this.apiUrl}/listar`, request, {
        params,
        ...this.addHeaderToken(),
      })
      .pipe(catchError(this.handleError));
  }

  /** Mesmos filtros da listagem, sem paginação; o servidor corta no teto e marca isTruncado. */
  public getListaPrint(request: NotaFiscalSaidaListarRequest): Observable<NotaFiscalSaidaListarPrintResponse> {
    return this.httpClient
      .post<NotaFiscalSaidaListarPrintResponse>(`${this.apiUrl}/lista/print`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Agregado de lookups e configurações do documento: uma chamada para abrir a tela. */
  public getConfiguracoes(): Observable<GetConfiguracoesNotaFiscalSaidaResponse> {
    return this.httpClient
      .get<GetConfiguracoesNotaFiscalSaidaResponse>(`${this.apiUrl}/configuracoes`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** A nota completa para edição. */
  public getPorId(idNFeSaida: number): Observable<GetNotaFiscalSaidaResponse> {
    return this.httpClient
      .get<GetNotaFiscalSaidaResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Confere a nota e devolve as divergências. Grava o retrato só na nota Aguardando transmissão, Rejeitada ou Em transmissão com transmissão não realizada, com permissão de
   * incluir ou alterar: a tela chama ao abrir a nota editável.
   */
  public conferencia(idNFeSaida: number): Observable<NFeSaidaConferenciaResponse> {
    return this.httpClient
      .get<NFeSaidaConferenciaResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/conferencia`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** O snapshot gravado e as tags resolvidas, para a tela de visualização (sem lookups). */
  public getVisualizacao(idNFeSaida: number): Observable<GetNotaFiscalSaidaVisualizacaoResponse> {
    return this.httpClient
      .get<GetNotaFiscalSaidaVisualizacaoResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/visualizacao`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Transmite a nota gravada (T13): o servidor confere status, dados, pagamento, divergências, cálculo e leiaute e põe a
   * nota na fila (Em transmissão). Assíncrono: o retorno da SEFAZ chega depois, pelo `listarSituacao`.
   */
  public transmitir(idNFeSaida: number): Observable<TransmitirNotaFiscalSaidaResponse> {
    return this.httpClient
      .post<TransmitirNotaFiscalSaidaResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/transmitir`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Situação das notas (status, último retorno da SEFAZ e substatus da fila): listagem e documento. */
  public listarSituacao(listaIdNFeSaida: number[]): Observable<ListarSituacaoNotaFiscalSaidaResponse> {
    const request = new ListarSituacaoNotaFiscalSaidaRequest();
    request.listaIdNFeSaida = listaIdNFeSaida;
    return this.httpClient
      .post<ListarSituacaoNotaFiscalSaidaResponse>(`${this.apiUrl}/situacao/listar`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Erro de comunicação ou transmitida sem retorno: antecipa a consulta da nota na SEFAZ (o resultado aparece em instantes). */
  public consultarRetorno(idNFeSaida: number): Observable<ConsultarRetornoNotaFiscalSaidaResponse> {
    return this.httpClient
      .post<ConsultarRetornoNotaFiscalSaidaResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/consultarRetorno`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Lote de transmissão (T14): as notas elegíveis com quantidade e valor; o envio é o `transmitir` nota a nota. */
  public prepararTransmissaoLote(request: PrepararTransmissaoLoteNotaFiscalSaidaRequest): Observable<PrepararTransmissaoLoteNotaFiscalSaidaResponse> {
    return this.httpClient
      .post<PrepararTransmissaoLoteNotaFiscalSaidaResponse>(`${this.apiUrl}/transmitir/lote/preparar`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Ocorrências da nota, por data do fato desc. */
  public getHistorico(idNFeSaida: number): Observable<GetNotaFiscalSaidaHistoricoResponse> {
    return this.httpClient
      .get<GetNotaFiscalSaidaHistoricoResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/historico`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Grava a anotação do usuário no histórico da nota (tipo Anotação, origem Usuário), em qualquer status. */
  public criarOcorrencia(idNFeSaida: number, request: CriarNotaFiscalSaidaOcorrenciaRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/ocorrencia`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** O cliente inteiro (cadastro, financeiro e últimas notas) para o card e o modal do Destinatário, com a permissão da NF-e. */
  public getCliente(idPessoa: number): Observable<GetClienteNotaFiscalSaidaResponse> {
    return this.httpClient
      .get<GetClienteNotaFiscalSaidaResponse>(`${this.apiUrl}/cliente/idPessoa/${idPessoa}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Inclui (idNFeSaida 0) ou altera a nota; a numeração é reservada pelo servidor no primeiro salvamento. */
  public salvar(request: SalvarNotaFiscalSaidaRequest): Observable<SalvarNotaFiscalSaidaResponse> {
    return this.httpClient
      .post<SalvarNotaFiscalSaidaResponse>(`${this.apiUrl}/salvar`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Exclui as notas que nunca foram autorizadas (Aguardando transmissão, Rejeitada ou Em transmissão com transmissão não realizada, sem estoque e financeiro lançados) e
   * libera os números reservados. Tudo ou nada: a tela manda só as elegíveis; uma nota só é o lote com um id.
   */
  public excluirEmLote(idsNFeSaida: number[]): Observable<ExcluirNotaFiscalSaidaResponse> {
    const request = new NotaFiscalSaidaExcluirLoteRequest();
    request.idsNFeSaida = idsNFeSaida;
    return this.httpClient
      .post<ExcluirNotaFiscalSaidaResponse>(`${this.apiUrl}/excluir/lote`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Só a resolução (sem executar fórmulas): natureza, exceções, regra e parâmetros efetivos do item-alvo
   * (`identificadorItemAlvo`) e do documento. Usado para reaplicar a natureza no item e em "Ver detalhes da regra".
   */
  public resolverCalculoItem(request: NFeSaidaCalculoPreviaRequest): Observable<CalculoNFeSaidaResolverResponse> {
    return this.httpClient
      .post<CalculoNFeSaidaResolverResponse>(`${this.apiUrl}/calculo/item/resolver`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Prévia com o item-alvo (`identificadorItemAlvo`): a nota inteira calculada, sem gravar. */
  public calcularPreviaItem(request: NFeSaidaCalculoPreviaRequest): Observable<NFeSaidaCalculoPreviaResponse> {
    return this.httpClient
      .post<NFeSaidaCalculoPreviaResponse>(`${this.apiUrl}/calculo/item/previa`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Prévia do documento: todos os itens e os totais calculados, sem gravar. */
  public calcularPrevia(request: NFeSaidaCalculoPreviaRequest): Observable<NFeSaidaCalculoPreviaResponse> {
    return this.httpClient
      .post<NFeSaidaCalculoPreviaResponse>(`${this.apiUrl}/calculo/previa`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // Financeiro da NF-e de saída (NotaFiscalSaidaFinanceiroController, mesma rota NotaFiscalSaida), como o financeiro do
  // pedido fica no service do ProcessoVenda.

  /** Lança o financeiro da nota autorizada no Finanças (permissão de incluir nota fiscal). */
  public lancarFinanceiro(idNFeSaida: number, request: LancarFinanceiroNotaFiscalSaidaRequest): Observable<FinanceiroNotaFiscalSaidaResponse> {
    return this.httpClient.post<FinanceiroNotaFiscalSaidaResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/financeiro/lancar`, request, {
      ...this.addHeaderToken(),
    }).pipe(catchError(this.handleError));
  }

  /** Exclui o financeiro lançado pela nota, tudo ou nada, com justificativa (permissão de excluir o financeiro). */
  public excluirFinanceiro(idNFeSaida: number, request: ExcluirFinanceiroNotaFiscalSaidaRequest): Observable<FinanceiroNotaFiscalSaidaResponse> {
    return this.httpClient.post<FinanceiroNotaFiscalSaidaResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/financeiro/excluir`, request, {
      ...this.addHeaderToken(),
    }).pipe(catchError(this.handleError));
  }

  /** Financeiro ativo da nota no Finanças, com o motivo que impede excluir cada título e movimentação. */
  public getFinanceiro(idNFeSaida: number): Observable<GetFinanceiroNotaFiscalSaidaResponse> {
    return this.httpClient.get<GetFinanceiroNotaFiscalSaidaResponse>(`${this.apiUrl}/idNFeSaida/${idNFeSaida}/financeiro`, {
      ...this.addHeaderToken(),
    }).pipe(catchError(this.handleError));
  }
}
